import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const NarrativeSummaryPanel = ({ 
  selectedNarrative, 
  onExportReport,
  onEscalate,
  onViewEvidence 
}) => {
  const [activeTab, setActiveTab] = useState('summary');

  // Mock narrative data
  const narrativeData = {
    id: 'n1',
    title: 'Energy Crisis Manipulation Campaign',
    category: 'Economic',
    status: 'Active',
    riskLevel: 'High',
    confidence: 85,
    firstDetected: '2024-09-01',
    lastActivity: '2024-09-21',
    affectedCountries: ['Germany', 'Poland', 'Czech Republic'],
    totalClaims: 12,
    verifiedFalse: 8,
    partiallyTrue: 3,
    unverified: 1,
    summary: `This coordinated disinformation campaign targets European energy security by spreading false narratives about renewable energy failures and promoting dependency on specific energy sources.\n\nThe campaign shows sophisticated coordination across multiple platforms and languages, with synchronized messaging peaks during key policy announcements.\n\nKey tactics include manipulated statistics, out-of-context imagery, and amplification through inauthentic networks.`,
    keyClaims: [
      {
        id: 'c1',
        text: 'Wind turbines cause widespread blackouts across Germany',
        verdict: 'False',
        confidence: 92,
        sources: 15,
        countries: ['DE', 'PL'],
        viralityScore: 78
      },
      {
        id: 'c2',
        text: 'Solar panels are causing environmental disasters in Europe',
        verdict: 'Misleading',
        confidence: 87,
        sources: 12,
        countries: ['DE', 'CZ'],
        viralityScore: 65
      },
      {
        id: 'c3',
        text: 'EU energy policies will lead to economic collapse',
        verdict: 'False',
        confidence: 89,
        sources: 18,
        countries: ['DE', 'PL', 'CZ'],
        viralityScore: 84
      }
    ],
    coordinationSignals: [
      {
        type: 'Synchronized Posting',
        strength: 0.89,
        description: 'Content posted within 2-hour windows across platforms',
        evidence: 'Temporal analysis shows 89% correlation in posting times'
      },
      {
        type: 'Shared Assets',
        strength: 0.76,
        description: 'Identical images and videos used across accounts',
        evidence: 'Visual similarity analysis identified 23 shared media assets'
      },
      {
        type: 'Language Patterns',
        strength: 0.82,
        description: 'Similar phrasing and terminology across languages',
        evidence: 'NLP analysis shows 82% semantic similarity in translations'
      }
    ],
    trendingTopics: [
      { topic: '#EnergyScam', mentions: 2847, sentiment: -0.78 },
      { topic: '#GreenLies', mentions: 1923, sentiment: -0.85 },
      { topic: '#EUEnergyFail', mentions: 1456, sentiment: -0.72 }
    ]
  };

  const tabs = [
    { id: 'summary', label: 'Summary', icon: 'FileText' },
    { id: 'claims', label: 'Key Claims', icon: 'List' },
    { id: 'coordination', label: 'Coordination', icon: 'GitBranch' },
    { id: 'trends', label: 'Trends', icon: 'TrendingUp' }
  ];

  const getVerdictColor = (verdict) => {
    switch (verdict?.toLowerCase()) {
      case 'false': return 'text-destructive';
      case 'misleading': return 'text-warning';
      case 'true': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'bg-destructive text-destructive-foreground';
      case 'medium': return 'bg-warning text-warning-foreground';
      case 'low': return 'bg-success text-success-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (!selectedNarrative) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-soft p-8 text-center">
        <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Narrative Selected</h3>
        <p className="text-muted-foreground">
          Select a narrative from the map or clustering visualization to view detailed analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-foreground">{narrativeData?.title}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(narrativeData?.riskLevel)}`}>
                {narrativeData?.riskLevel} Risk
              </span>
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                {narrativeData?.status}
              </span>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span>Category: {narrativeData?.category}</span>
              <span>•</span>
              <span>Confidence: {narrativeData?.confidence}%</span>
              <span>•</span>
              <span>Countries: {narrativeData?.affectedCountries?.length}</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="AlertTriangle"
              iconPosition="left"
              onClick={() => onEscalate(narrativeData?.id)}
            >
              Escalate
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={() => onExportReport(narrativeData?.id)}
            >
              Export
            </Button>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border bg-muted/30">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`
              flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-smooth
              ${activeTab === tab?.id
                ? 'border-b-2 border-primary text-primary bg-card' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }
            `}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'summary' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{narrativeData?.totalClaims}</div>
                <div className="text-xs text-muted-foreground">Total Claims</div>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-destructive">{narrativeData?.verifiedFalse}</div>
                <div className="text-xs text-muted-foreground">Verified False</div>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-warning">{narrativeData?.partiallyTrue}</div>
                <div className="text-xs text-muted-foreground">Partially True</div>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-muted-foreground">{narrativeData?.unverified}</div>
                <div className="text-xs text-muted-foreground">Unverified</div>
              </div>
            </div>

            {/* Summary Text */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Analysis Summary</h4>
              <div className="prose prose-sm max-w-none">
                {narrativeData?.summary?.split('\n\n')?.map((paragraph, index) => (
                  <p key={index} className="text-sm text-muted-foreground mb-3">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Timeline</h4>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">First Detected:</span>
                  <span className="font-medium text-foreground">{narrativeData?.firstDetected}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">Last Activity:</span>
                  <span className="font-medium text-foreground">{narrativeData?.lastActivity}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'claims' && (
          <div className="space-y-4">
            {narrativeData?.keyClaims?.map((claim) => (
              <div key={claim?.id} className="p-4 border border-border rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-sm text-foreground font-medium mb-2">{claim?.text}</p>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span className={`font-semibold ${getVerdictColor(claim?.verdict)}`}>
                        {claim?.verdict}
                      </span>
                      <span>Confidence: {claim?.confidence}%</span>
                      <span>Sources: {claim?.sources}</span>
                      <span>Virality: {claim?.viralityScore}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    onClick={() => onViewEvidence(claim?.id)}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">Countries:</span>
                  <div className="flex space-x-1">
                    {claim?.countries?.map(country => (
                      <span 
                        key={country}
                        className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded"
                      >
                        {country}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'coordination' && (
          <div className="space-y-4">
            {narrativeData?.coordinationSignals?.map((signal, index) => (
              <div key={index} className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-foreground">{signal?.type}</h5>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-muted rounded-full h-2">
                      <div 
                        className="h-2 bg-primary rounded-full" 
                        style={{ width: `${signal?.strength * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground">
                      {(signal?.strength * 100)?.toFixed(0)}%
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{signal?.description}</p>
                <p className="text-xs text-muted-foreground italic">{signal?.evidence}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground">Trending Topics</h4>
            {narrativeData?.trendingTopics?.map((topic, index) => (
              <div key={index} className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-foreground">{topic?.topic}</span>
                  <span className="text-xs text-muted-foreground">{topic?.mentions} mentions</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${topic?.sentiment < -0.5 ? 'bg-destructive' : topic?.sentiment < 0 ? 'bg-warning' : 'bg-success'}`} />
                  <span className="text-xs font-medium text-foreground">
                    {topic?.sentiment > 0 ? '+' : ''}{(topic?.sentiment * 100)?.toFixed(0)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NarrativeSummaryPanel;